import {
  OrgGuideFileId,
  OrgGuideFileVersionId,
  OrgGuideFileVersionState,
  OrgId,
  asciiCompare,
  isOrgGuideFileVersionExtant,
} from "@hex/common";
import {
  EntityState,
  PayloadAction,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import { memoize } from "lodash";

import {
  OrgGuideFileMP,
  OrgGuideFileVersionMP,
  OrgGuideFilesMPModel,
  OrgGuideFilesMpInitFragment,
} from "../../org-guide-files-multiplayer/OrgGuideFilesMPModel";
import { RootState } from "../store";
import {
  getSelectorsForNonNullableEntityState,
  getSelectorsForNullableEntityState,
} from "../utils/index.js";

// *** State *****************************************************************
const modelAdapter = createEntityAdapter<OrgGuideFilesMPModel, OrgId>({
  selectId: (model) => model.orgId,
  sortComparer: (a, b) => asciiCompare(a.orgId, b.orgId),
});

const orgGuideFileAdapter = createEntityAdapter<OrgGuideFileMP>({
  sortComparer: (a, b) => asciiCompare(a.filePath, b.filePath),
});

const orgGuideFileVersionAdapter = createEntityAdapter<OrgGuideFileVersionMP>({
  sortComparer: (a, b) => asciiCompare(a.id, b.id),
});

/**
 * State for the OrgGuideFiles multiplayer slice. Entities are indexed by
 * orgId and represent the entire model state for a single org.
 */
type OrgGuideFilesMPSliceState = EntityState<OrgGuideFilesMPModel, OrgId>;

const initialState: OrgGuideFilesMPSliceState =
  // eslint-disable-next-line tree-shaking/no-side-effects-in-initialization -- this is okay
  modelAdapter.getInitialState();

// *** Actions ****************************************************************
type OrgGuideFilesAction<D = object> = PayloadAction<D & { orgId: OrgId }>;

const orgGuideFilesMPSlice = createSlice({
  name: "orgGuideFilesMP",
  initialState,
  reducers: {
    /** Load a new org guide files into the store for use. */
    initialize(state, action: PayloadAction<OrgGuideFilesMpInitFragment>) {
      const model: OrgGuideFilesMPModel = {
        orgId: action.payload.id,
        orgGuideFiles: orgGuideFileAdapter.getInitialState(),
        orgGuideFileVersions: orgGuideFileVersionAdapter.getInitialState(),
      };
      const { orgGuideFileVersions, orgGuideFiles } =
        action.payload.orgGuideFiles.reduce<{
          orgGuideFiles: OrgGuideFileMP[];
          orgGuideFileVersions: OrgGuideFileVersionMP[];
        }>(
          (acc, orgGuideFile) => {
            acc.orgGuideFiles.push({
              __typename: orgGuideFile.__typename,
              id: orgGuideFile.id,
              revision: orgGuideFile.revision,
              filePath: orgGuideFile.filePath,
              latestVersionId: orgGuideFile.latestVersion?.id ?? null,
              draftVersionId: orgGuideFile.draftVersion?.id ?? null,
            });
            if (
              isOrgGuideFileVersionExtant(orgGuideFile.draftVersion) &&
              orgGuideFile.draftVersion.state === OrgGuideFileVersionState.DRAFT
            ) {
              acc.orgGuideFileVersions.push({
                __typename: orgGuideFile.draftVersion.__typename,
                id: orgGuideFile.draftVersion.id,
                revision: orgGuideFile.draftVersion.revision,
                contentsText: orgGuideFile.draftVersion.contentsText,
                state: orgGuideFile.draftVersion.state,
                orgGuideFileId: orgGuideFile.id,
                externalSource: orgGuideFile.draftVersion.externalSource,
              });
            }
            return acc;
          },
          { orgGuideFiles: [], orgGuideFileVersions: [] },
        );
      model.orgGuideFiles = orgGuideFileAdapter.setAll(
        model.orgGuideFiles,
        orgGuideFiles,
      );
      model.orgGuideFileVersions = orgGuideFileVersionAdapter.setAll(
        model.orgGuideFileVersions,
        orgGuideFileVersions,
      );
      return modelAdapter.setOne(state, model);
    },

    /** Update the specified org guide file. */
    upsertOrgGuideFile(
      state,
      action: OrgGuideFilesAction<{
        orgGuideFile: OrgGuideFileMP;
      }>,
    ) {
      const model = selectModel(state, action.payload.orgId);
      if (model == null) return;
      model.orgGuideFiles = orgGuideFileAdapter.upsertOne(
        model.orgGuideFiles,
        action.payload.orgGuideFile,
      );
    },

    /** Remove the specified org guide file. */
    removeOrgGuideFile(
      state,
      action: OrgGuideFilesAction<{ orgGuideFileId: OrgGuideFileId }>,
    ) {
      const model = selectModel(state, action.payload.orgId);
      if (model == null) return;
      model.orgGuideFiles = orgGuideFileAdapter.removeOne(
        model.orgGuideFiles,
        action.payload.orgGuideFileId,
      );
    },

    /** Update the specified org guide file version. */
    upsertOrgGuideFileVersion(
      state,
      action: OrgGuideFilesAction<{
        orgGuideFileVersion: OrgGuideFileVersionMP;
      }>,
    ) {
      const model = selectModel(state, action.payload.orgId);
      if (model == null) return;
      model.orgGuideFileVersions = orgGuideFileVersionAdapter.upsertOne(
        model.orgGuideFileVersions,
        action.payload.orgGuideFileVersion,
      );
    },

    /** Remove the specified org guide file version. */
    removeOrgGuideFileVersion(
      state,
      action: OrgGuideFilesAction<{
        orgGuideFileVersionId: OrgGuideFileVersionId;
      }>,
    ) {
      const model = selectModel(state, action.payload.orgId);
      if (model == null) return;
      model.orgGuideFileVersions = orgGuideFileVersionAdapter.removeOne(
        model.orgGuideFileVersions,
        action.payload.orgGuideFileVersionId,
      );
    },
  },
});

export const orgGuideFilesMPReducer = orgGuideFilesMPSlice.reducer;

export const orgGuideFilesMPActions = orgGuideFilesMPSlice.actions;

// *** Selectors **************************************************************

function selectSlice(state: RootState): OrgGuideFilesMPSliceState {
  return state.orgGuideFilesMP;
}
function selectModel(
  state: OrgGuideFilesMPSliceState,
  orgId: OrgId,
): OrgGuideFilesMPModel | null {
  const model = state.entities[orgId];
  return model ?? null;
}

/** Selectors for the OrgGuideFiles multiplayer slice */
export const orgGuideFilesMPSelectors = {
  /** Selectors for a single model state for a given orgId */
  org: memoize((orgId: OrgId) => ({
    /** Select the entire model state */
    select: (s: RootState): OrgGuideFilesMPModel | null =>
      selectModel(selectSlice(s), orgId),
  })),
  /** Selectors for the OrgGuideFileMP entities for a given orgId */
  orgGuideFile: memoize((orgId: OrgId) => ({
    ...getSelectorsForNullableEntityState(
      orgGuideFileAdapter,
      (s: RootState) => selectModel(selectSlice(s), orgId)?.orgGuideFiles,
    ),
  })),
  /** Selectors for the OrgGuideFileVersionMP entities for a given orgId */
  orgGuideFileVersion: memoize((orgId: OrgId) => ({
    ...getSelectorsForNullableEntityState(
      orgGuideFileVersionAdapter,
      (s: RootState) =>
        selectModel(selectSlice(s), orgId)?.orgGuideFileVersions,
    ),
  })),
  /** Selectors for entities within a single non-null model */
  model: {
    /** Selectors for the OrgGuideFileMP entities for a given model */
    orgGuideFile: {
      ...getSelectorsForNonNullableEntityState(
        orgGuideFileAdapter,
        (root: OrgGuideFilesMPModel) => root.orgGuideFiles,
      ),
    },
    /** Selectors for the OrgGuideFileVersionMP entities for a given model */
    orgGuideFileVersion: {
      ...getSelectorsForNonNullableEntityState(
        orgGuideFileVersionAdapter,
        (root: OrgGuideFilesMPModel) => root.orgGuideFileVersions,
      ),
    },
  },
};
